import { useEffect, useRef, useState } from 'react';

export const useAfterpayVisibility = (sku?: string) => {
    const [isPlacementVisible, setIsPlacementVisible] = useState<boolean>(false);
    const [hasTimedOut, setHasTimedOut] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Detect when the placement actually renders content inside its shadow root
    useEffect(() => {
        // Reset state on sku change
        setIsPlacementVisible(false);
        setHasTimedOut(false);

        const containerEl = containerRef.current;
        if (!containerEl) return;

        const timeoutDuration = 10000; // 10 seconds timeout tested on 4g slow connection
        let allObserversDisconnected = false;
        let shadowRootObserver: MutationObserver | null = null;
        let containerObserver: MutationObserver | null = null;

        const cleanup = () => {
            if (!allObserversDisconnected) {
                allObserversDisconnected = true;
                shadowRootObserver?.disconnect();
                containerObserver?.disconnect();
            }
        };

        const handleContentFound = () => {
            if (!allObserversDisconnected) {
                setIsPlacementVisible(true);
                cleanup();
            }
        };

        const isContentPresent = (placementEl: Element) => Boolean(placementEl.shadowRoot?.childElementCount);

        const tryObservePlacement = (): boolean => {
            const placementEl = containerEl.querySelector('square-placement');
            if (!placementEl) return false;

            const shadowRoot = placementEl.shadowRoot;
            if (!shadowRoot) return false;

            // Check if content is already present
            if (isContentPresent(placementEl)) {
                handleContentFound();
                return true;
            }

            // Watch for content to appear in shadow root
            shadowRootObserver = new MutationObserver(() => {
                if (isContentPresent(placementEl)) {
                    handleContentFound();
                }
            });

            shadowRootObserver.observe(shadowRoot, {
                childList: true,
                subtree: true,
            });

            return true;
        };

        // Wait for Afterpay script to load and custom element to be defined
        const waitForAfterpayScript = async (): Promise<void> => {
            if (typeof window === 'undefined' || !window.customElements) {
                return; // SSR or no custom elements support
            }

            // Wait for the square-placement custom element to be defined
            await window.customElements.whenDefined('square-placement');
        };

        const startObservation = () => {
            if (tryObservePlacement()) {
                // Success - cleanup will be handled by timeout or content found
            } else {
                // Element or shadowRoot not ready - watch container for changes
                containerObserver = new MutationObserver(() => {
                    if (tryObservePlacement()) {
                        // Successfully set up placement observer, stop watching container
                        containerObserver?.disconnect();
                        containerObserver = null;
                    }
                });

                containerObserver.observe(containerEl, {
                    childList: true,
                    subtree: true,
                });
            }
        };

        // Wait for Afterpay script, then start observation
        waitForAfterpayScript().then(() => {
            if (!allObserversDisconnected) {
                startObservation();
            }
        });

        // Set timeout to handle cases where content never loads
        const timeoutId = window.setTimeout(() => {
            if (!allObserversDisconnected) {
                setHasTimedOut(true);
                cleanup();
            }
        }, timeoutDuration);

        return () => {
            window.clearTimeout(timeoutId);
            cleanup();
        };
    }, [sku]);

    return {
        containerRef,
        isPlacementVisible,
        hasTimedOut,
    };
};
